import { Button, ConfigProvider, Flex, Popover } from "antd";
import {
  Header,
  CustomText,
  Logo,
  MediaItem,
  Avatar,
  SubtitlesItem,
  TitleItem,
  Shape,
  ParameteredText,
  Table,
  TableDividerStyle,
} from "../../types";
import { ReactNode } from "react";
import {
  AlignCenterVerticalIcon,
  AlignLeftIcon,
  AlignRightIcon,
  AlignTopIcon,
  AlignBottomIcon,
  AlignCenterHorizontalIcon,
} from "components/icons";

import { useAntTheme } from "hooks/useAntTheme/";
import { getRelativePosition } from "../../helpers";
import { ButtonsWrapper } from "../StoryboardMenuTabs/ButtonWrapper";

interface AlignButton {
  key: string;
  icon: ReactNode;
  onClick: () => void;
  isActive: boolean;
}

type ItemToAlign =
  | SubtitlesItem
  | TitleItem
  | Avatar
  | MediaItem
  | Logo
  | CustomText
  | Shape
  | ParameteredText
  | Table
  | TableDividerStyle;

type RelativeItem = Header;

export const ItemAlignmentFeature = ({
  pickedItem,
  onChange,
  previewWidth,
  previewHeight,
  popover = false,
  relativeItem,
}: {
  pickedItem: ItemToAlign;
  onChange: (value: Pick<ItemToAlign, "position">) => void;
  previewWidth: number;
  previewHeight: number;
  popover?: boolean;
  relativeItem?: RelativeItem;
}) => {
  const { theme } = useAntTheme();

  const itemHasHeight = pickedItem.size.height !== null;
  const itemHeight = (pickedItem.size.height ?? 0) * previewHeight;
  const hasStroke = "stroke" in pickedItem;

  const itemStrokeWidth = hasStroke
    ? Math.round((pickedItem.stroke?.width ?? 0) * previewWidth)
    : 0;

  const positionStartY =
    (relativeItem?.position.y ?? 0) * previewHeight + itemStrokeWidth;

  const positionEndY =
    (relativeItem && relativeItem.position.y === 0
      ? relativeItem.size.height * previewHeight
      : previewHeight) - itemStrokeWidth;

  const positionStartX =
    (relativeItem?.position.x ?? 0) * previewWidth + itemStrokeWidth;
  const positionEndX =
    (relativeItem && relativeItem.position.x === 0
      ? relativeItem.size.width * previewWidth
      : previewWidth) - itemStrokeWidth;

  const updateAsset = (position: {
    horizontalPosition?: number;
    verticalPosition?: number;
  }) => {
    const {
      horizontalPosition = pickedItem.position.x,
      verticalPosition = pickedItem.position.y,
    } = position;
    const updatedItemToAlign = {
      ...pickedItem,
      position: {
        x: horizontalPosition,
        y: verticalPosition,
      },
    };
    onChange(updatedItemToAlign);
  };

  // Helper to compare positions with tolerance for floating point precision
  const isPositionEqual = (a: number, b: number, tolerance = 0.001) =>
    Math.abs(a - b) < tolerance;

  // Current item position
  const currentX = pickedItem.position.x;
  const currentY = pickedItem.position.y;
  const itemWidth = pickedItem.size.width;

  // Calculate expected positions for horizontal alignment
  const leftX = getRelativePosition(positionStartX, previewWidth);
  const centerX = getRelativePosition(
    (positionStartX + positionEndX) / 2 - (itemWidth * previewWidth) / 2,
    previewWidth
  );
  const rightX = getRelativePosition(
    positionEndX - itemWidth * previewWidth,
    previewWidth
  );

  // Calculate expected positions for vertical alignment
  const topY = getRelativePosition(positionStartY, previewHeight);
  const centerY = getRelativePosition(
    (positionStartY + positionEndY) / 2 - itemHeight / 2,
    previewHeight
  );
  const bottomY = getRelativePosition(positionEndY - itemHeight, previewHeight);

  const alignButtonsHorizontal: AlignButton[] = [
    {
      key: "left",
      icon: <AlignLeftIcon />,
      onClick: () => {
        updateAsset({
          horizontalPosition: leftX,
        });
      },
      isActive: isPositionEqual(currentX, leftX),
    },
    {
      key: "centerHorizontal",
      icon: <AlignCenterHorizontalIcon />,
      onClick: () => {
        updateAsset({
          horizontalPosition: centerX,
        });
      },
      isActive: isPositionEqual(currentX, centerX),
    },
    {
      key: "right",
      icon: <AlignRightIcon />,
      onClick: () => {
        updateAsset({
          horizontalPosition: rightX,
        });
      },
      isActive: isPositionEqual(currentX, rightX),
    },
  ];

  const alignButtonsVertical: AlignButton[] = [
    {
      key: "top",
      icon: <AlignTopIcon />,
      onClick: () => {
        updateAsset({
          verticalPosition: topY,
        });
      },
      isActive: isPositionEqual(currentY, topY),
    },
    {
      key: "centerVertical",
      icon: <AlignCenterVerticalIcon />,
      onClick: () => {
        updateAsset({
          verticalPosition: centerY,
        });
      },
      isActive: isPositionEqual(currentY, centerY),
    },
    {
      key: "bottom",
      icon: <AlignBottomIcon />,
      onClick: () => {
        updateAsset({
          verticalPosition: bottomY,
        });
      },
      isActive: isPositionEqual(currentY, bottomY),
    },
  ];

  return (
    <ConfigProvider
      theme={{
        components: {
          Button: {
            defaultColor: theme.colorTextBase,
            colorBorder: "transparent",
            defaultBg: "transparent",
            defaultBorderColor: "transparent",
            defaultHoverColor: theme.colorTextBase,
            defaultHoverBorderColor: "transparent",
            defaultHoverBg: popover
              ? theme.colorBgContainerDisabled
              : theme.colorBgBase,
            colorBgContainerDisabled: "transparent",
          },
        },
      }}
    >
      <ItemAlignmentWrapper popover={popover}>
        <Flex gap={12} vertical align="start">
          <ButtonsWrapper $hasBackground={!popover}>
            {alignButtonsHorizontal.map((button) => (
              <Button
                data-testid="align-button"
                key={button.key}
                icon={button.icon}
                onClick={button.onClick}
                style={{
                  height: 24,
                  width: 32,
                  backgroundColor: button.isActive
                    ? theme.colorBgBase
                    : undefined,
                }}
              />
            ))}
          </ButtonsWrapper>
          {itemHasHeight && (
            <ButtonsWrapper $hasBackground={!popover}>
              {alignButtonsVertical.map((button) => (
                <Button
                  data-testid="align-button"
                  key={button.key}
                  icon={button.icon}
                  onClick={button.onClick}
                  style={{
                    height: 24,
                    width: 32,
                    backgroundColor: button.isActive
                      ? theme.colorBgBase
                      : undefined,
                  }}
                />
              ))}
            </ButtonsWrapper>
          )}
        </Flex>
      </ItemAlignmentWrapper>
    </ConfigProvider>
  );
};

const ItemAlignmentWrapper = ({
  children,
  popover,
}: {
  children: ReactNode;
  popover?: boolean;
}) => {
  const content = popover ? (
    <Popover
      content={children}
      getPopupContainer={(triggerNode) =>
        triggerNode.parentElement ?? triggerNode
      }
      trigger="click"
    >
      <Button
        icon={
          <Flex align="center" justify="center">
            <AlignCenterHorizontalIcon width={20} height={20} />
          </Flex>
        }
        size="middle"
        type="text"
      />
    </Popover>
  ) : (
    children
  );

  return content;
};
